import {
  AutocompleteOption,
  InitialValue,
  MultiAutocomplete,
  MultiOnChangeProp,
  SingleAutocomplete,
  SingleOnChangeProp,
  useUserState
} from '@checkit/react-components';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import {
  getCheckpointTypes,
  isGetCheckpointTypesSuccess
} from '../../checkpointTypes/actions';
import { useAxiosDispatch } from '../../middleware/axios';
import { appMessages } from '../../translations';

interface BaseProps {
  className?: string;
  label?: string;
  fullWidth?: boolean;
}

export interface MultipleProps extends BaseProps {
  initialValue?: InitialValue[];
  onChange: MultiOnChangeProp;
  multiple: true;
}

export interface SingularProps extends BaseProps {
  initialValue?: InitialValue;
  onChange: SingleOnChangeProp;
  multiple: false;
  placeholder?: string;
}

type Props = SingularProps | MultipleProps;

const isMultipleProps = (props: Props): props is MultipleProps => props.multiple;

const messages = defineMessages({
  'autocomplete.checkpointType.label': {
    defaultMessage: 'Checkpoint Type',
    id: 'autocomplete.checkpointType.label'
  }
});

export const CheckpointTypeAutocomplete = (props: Props) => {
  const {className, label, fullWidth} = props;
  const {formatMessage} = useIntl();
  const dispatch = useAxiosDispatch( );

  const getOptions = (text: string, limit: number) => {
    return dispatch(getCheckpointTypes({
      filter: text,
      take: limit,
      transient: true
    })).then(function handleResult(result) {
      if (isGetCheckpointTypesSuccess(result)) {
        const checkpointTypes = result.payload.data;
        return checkpointTypes.map(checkpointType => ({
          code: checkpointType.code,
          name: checkpointType.name
        }));
      } else {
        const status = result.error.code || result.error.message;
        throw new Error(`Failed to load checkpoint types: ${status}`);
      }
    });
  };

  const computedLabel = label === undefined ? formatMessage(messages['autocomplete.checkpointType.label']) : label;

  const userState = useUserState();

  // This covers the case where props.multiple is true but the feature is disabled
  // Ideally it would be inside the if but conditional hooks are not allowed
  const onSingleTypeChange = useCallback((event: React.ChangeEvent<{}>, value: AutocompleteOption | null) => {
    (props.onChange as MultiOnChangeProp)(event, value ? [value] : []);
  }, [props.onChange]);

  const qrScanning = userState.features.settings.qrScanning;
  const multipleCheckpointTypes = qrScanning ? qrScanning.MULTIPLE_CHECKPOINT_TYPES === 'true' : false;

  if (isMultipleProps(props)) {
    if (multipleCheckpointTypes) {
      return (
        <MultiAutocomplete
          fullWidth={fullWidth}
          initialValues={props.initialValue || []}
          onChange={props.onChange}
          getOptions={getOptions}
          className={className}
          label={computedLabel}
          moreResultsText={formatMessage(appMessages['autocomplete.tooManyResults'])}
        />
      );
    } else {
      return (
        <SingleAutocomplete
          fullWidth={fullWidth}
          initialValue={props.initialValue ? props.initialValue[0] : undefined}
          onChange={onSingleTypeChange}
          getOptions={getOptions}
          className={className}
          label={computedLabel}
          moreResultsText={formatMessage(appMessages['autocomplete.tooManyResults'])}
        />
      );
    }
  } else {
    return (
      <SingleAutocomplete
        fullWidth={fullWidth}
        initialValue={props.initialValue}
        onChange={props.onChange}
        getOptions={getOptions}
        className={className}
        label={computedLabel}
        placeholder={props.placeholder}
        moreResultsText={formatMessage(appMessages['autocomplete.tooManyResults'])}
      />
    );
  }
};
